import { api } from './http';
import { API_ENDPOINTS } from '@/config/constants';

// API request types based on swagger specification
export interface ImportMappingRequest {
  file_to_form_mappings: Array<{
    csv_file_id: number | string;
    csv_file_version_id?: number;
    form_id: string;
    match_type: 'exact' | 'fuzzy' | 'manual';
    nested_path?: string; // JSON string with nested path structure
  }>;
  column_mappings: Array<{
    csv_file_id: number | string;
    csv_file_version_id?: number;
    column_name: string;
    mapping_type: 'metadata' | 'formField' | 'ignored';
    form_id: string;
    study_form_id: number;
    metadata_type?:
      | 'subjectData'
      | 'externalSubjectId'
      | 'subjectNumber'
      | 'site'
      | 'siteNumber'
      | 'siteName'
      | 'formId'
      | 'mainFormId'
      | 'deletedForm'
      | 'formIndex'
      | 'trash';
    target_field?: string;
  }>;
  subform_relationships: Array<{
    subform_id: string;
    parent_form_id: string;
    is_valid: boolean;
  }>;
}

// API response types
export interface ImportMappingResponse {
  id: number;
  study_id: number;
  status?: 'created' | 'valid_for_import' | 'invalid_for_import' | 'uploaded' | 'failed';
  import_validation_errors?: 
    | {
        [studyFormId: string]: string;
      }
    | {
        forms?: Record<string, string>;
        files?: Record<string, string>;
      }
    | Record<string, unknown>;
  created_by: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
  };
  created_at: string;
  updated_at: string;
  file_to_form_mappings: Array<{
    id: number;
    import_mapping_id: number;
    study_form_id: number;
    csv_file_id: number;
    match_type: string;
    nested_path?: string; // JSON string with nested path structure
  }>;
  column_mappings: Array<{
    id: number;
    import_mapping_id: number;
    study_form_id: number;
    csv_file_id: number;
    column_name: string;
    mapping_type: string;
    metadata_type: string | null;
    target_field: string;
  }>;
  subform_relationships: Array<{
    id: number;
    import_mapping_id: number;
    subform_id: number;
    parent_form_id: number;
    is_valid: boolean;
  }>;
}

// Import status types (for future use when we add import execution)
export interface ImportStatusResponse {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  current_file?: string;
  current_step?: string;
  sites_created: number;
  subjects_created: number;
  forms_processed: number;
  records_imported: number;
  total_records: number;
  estimated_time_remaining?: number;
  errors?: Array<{
    file_id: string;
    message: string;
    details?: string;
    row?: number;
    column?: string;
  }>;
}

// Dry-run validation response types
export interface DryRunValidationResponse {
  is_valid: boolean;
  errors: Array<{
    file_id: string;
    message: string;
    details?: string;
    row?: number;
    column?: string;
    severity: 'error' | 'warning';
  }>;
  warnings: Array<{
    file_id: string;
    message: string;
    details?: string;
    row?: number;
    column?: string;
    severity: 'warning';
  }>;
  summary: {
    total_files: number;
    mapped_files: number;
    total_columns: number;
    form_field_mappings: number;
    metadata_mappings: number;
    validation_time_ms: number;
  };
  preview_data?: {
    [formId: string]: {
      sample_rows: Array<{
        [fieldName: string]: unknown;
      }>;
      total_rows: number;
      mapped_fields: number;
      validation_errors: Array<{
        row: number;
        column: string;
        message: string;
        severity: 'error' | 'warning';
      }>;
    };
  };
}

/**
 * Create a new import mapping
 */
export async function createImportMapping(
  requestData: ImportMappingRequest
): Promise<ImportMappingResponse> {
  const url = API_ENDPOINTS.IMPORT_MAPPINGS;
  const response = await api.post(url, requestData);

  if (response.data && response.data.data) {
    return response.data.data;
  }

  // Fallback for direct response
  return response.data;
}

/**
 * Get an import mapping by ID
 */
export async function getImportMapping(
  mappingId: string | number
): Promise<ImportMappingResponse> {
  const url = `${API_ENDPOINTS.IMPORT_MAPPINGS}/${mappingId}`;
  const response = await api.get(url);

  if (response.data && response.data.data) {
    return response.data.data;
  }

  // Fallback for direct response
  return response.data;
}

/**
 * Update an import mapping
 */
export async function updateImportMapping(
  mappingId: string | number,
  requestData: ImportMappingRequest
): Promise<ImportMappingResponse> {
  const url = `${API_ENDPOINTS.IMPORT_MAPPINGS}/${mappingId}`;
  const response = await api.put(url, requestData);

  if (response.data && response.data.data) {
    return response.data.data;
  }

  // Fallback for direct response
  return response.data;
}

/**
 * Execute dry-run validation for import mappings
 */
export async function executeDryRunValidation(
  mappingId: string | number,
  options?: {
    validate_data?: boolean;
    generate_preview?: boolean;
    max_preview_rows?: number;
  }
): Promise<DryRunValidationResponse> {
  const url = `${API_ENDPOINTS.IMPORT_MAPPINGS}/${mappingId}/execute`;
  const response = await api.post(url, {
    dry_run: true,
    validate_data: options?.validate_data ?? true,
    generate_preview: options?.generate_preview ?? true,
    max_preview_rows: options?.max_preview_rows ?? 10,
  });

  if (response.data && response.data.data) {
    return response.data.data;
  }

  // Fallback for direct response
  return response.data;
}

/**
 * Execute import for import mappings
 */
export async function executeImport(
  mappingId: string | number
): Promise<{ import_job_id: string }> {
  const url = `${API_ENDPOINTS.IMPORT_MAPPINGS}/${mappingId}/execute`;
  const response = await api.post(url, {
    dry_run: false,
  });

  if (response.data && response.data.data) {
    return response.data.data;
  }

  // Fallback for direct response
  return response.data;
}

/**
 * Get import status (for future use when backend implements this endpoint)
 */
export async function getImportStatus(
  mappingId: string | number
): Promise<ImportStatusResponse> {
  const url = `${API_ENDPOINTS.IMPORT_MAPPINGS}/${mappingId}/status`;
  const response = await api.get(url);

  if (response.data && response.data.data) {
    return response.data.data;
  }

  // Fallback for direct response
  return response.data;
}
